/**
 * SourceTypeSwitchConfirmDialog - Confirmation dialog for switching between
 * file-based rules and view-based source in the RuleBuilder.
 *
 * Warns users that switching source types will clear existing rules and
 * column configurations.
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

export type SwitchDirection = 'to-view' | 'to-file';

interface SourceTypeSwitchConfirmDialogProps {
  open: boolean;
  direction: SwitchDirection;
  onConfirm: () => void;
  onCancel: () => void;
}

export const SourceTypeSwitchConfirmDialog: React.FC<SourceTypeSwitchConfirmDialogProps> = ({
  open,
  direction,
  onConfirm,
  onCancel,
}) => {
  const title =
    direction === 'to-view'
      ? 'Switch to View-Based Source?'
      : 'Switch to File-Based Rules?';

  const description =
    direction === 'to-view'
      ? 'Switching to a view-based source will remove your existing file-based rules and column configurations. This cannot be undone.'
      : 'Switching to file-based rules will remove your existing view-based source configuration and column configurations. This cannot be undone.';

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
